import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, FileText, Award, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { OnbBadge } from "./AdminOnboarding";

const ORI_STATUSES = ["未確認", "説明済み", "本人確認済み", "管理者確認済み"];
const OVERRIDE_STATUSES = ["未開始", "受講中", "研修修了", "オリエンテーション未完了", "管理者確認待ち", "修了", "期限超過"];

function OrientationRow({ uid, item, onSaved }) {
  const [s, setS] = useState(item);
  const save = async () => {
    try {
      await api.put(`/onboarding/employees/${uid}/orientation/${item.item_id}`, {
        status: s.status, explainer: s.explainer, note: s.note, confirmed_date: s.confirmed_date,
      });
      toast.success("保存しました"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const confirmMgr = async () => {
    try { await api.put(`/onboarding/employees/${uid}/orientation/${item.item_id}`, { manager_confirmed: true }); toast.success("管理者確認済みにしました"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <TableRow data-testid={`ori-row-${item.item_id}`}>
      <TableCell className="font-medium text-slate-700">{item.name}</TableCell>
      <TableCell>
        <Select value={s.status} onValueChange={(v) => setS({ ...s, status: v })}>
          <SelectTrigger className="h-8 w-36" data-testid={`ori-status-${item.item_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{ORI_STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
        </Select>
      </TableCell>
      <TableCell><Input value={s.explainer || ""} onChange={(e) => setS({ ...s, explainer: e.target.value })} placeholder="説明担当者" className="h-8 w-28" /></TableCell>
      <TableCell><Input value={s.note || ""} onChange={(e) => setS({ ...s, note: e.target.value })} placeholder="備考" className="h-8 w-32" /></TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-8" onClick={save} data-testid={`ori-save-${item.item_id}`}>保存</Button>
          <Button size="sm" variant="outline" className="h-8 text-green-700" onClick={confirmMgr} data-testid={`ori-confirm-${item.item_id}`}><ShieldCheck className="w-4 h-4" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function OnboardingDetail() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ovOpen, setOvOpen] = useState(false);
  const [ov, setOv] = useState({ status: "受講中", reason: "" });

  const load = useCallback(() => api.get(`/onboarding/employees/${userId}`).then(({ data }) => setD(data)), [userId]);
  useEffect(() => { load(); }, [load]);

  const managerConfirm = async () => {
    try { await api.post(`/onboarding/employees/${userId}/manager-confirm`); toast.success("管理者最終確認を行いました"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveOverride = async () => {
    try { await api.put(`/onboarding/employees/${userId}/status`, ov); toast.success("状態を変更しました"); setOvOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!d) return <Loading />;
  const eo = d.onboarding;
  const t = d.target;
  if (!eo) return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/onboarding")}><ArrowLeft className="w-4 h-4 mr-1" />戻る</Button>
      <div className="mt-6 text-center text-slate-400 py-16">{t?.name} さんは入社研修の対象ではありません</div>
    </div>
  );

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate("/onboarding")} className="mb-2" data-testid="onb-back"><ArrowLeft className="w-4 h-4 mr-1" />入社研修一覧</Button>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{t.job_type || "-"}・{t.employment_type || "-"}・入職日 {t.hire_date || "-"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <OnbBadge status={eo.status} />
          <Button size="sm" variant="outline" onClick={() => openPdf(`/onboarding/employees/${userId}/record.pdf`)} data-testid="onb-record-pdf"><FileText className="w-4 h-4 mr-1" />記録PDF</Button>
          <Button size="sm" variant="outline" onClick={() => openPdf(`/onboarding/employees/${userId}/orientation-sheet.pdf`)} data-testid="onb-ori-pdf"><FileText className="w-4 h-4 mr-1" />オリエン表</Button>
          {eo.cert_no && <Button size="sm" variant="outline" className="text-amber-700" onClick={() => openPdf(`/onboarding/employees/${userId}/certificate.pdf`)} data-testid="onb-cert-pdf"><Award className="w-4 h-4 mr-1" />修了証</Button>}
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3 mb-6">
        {[["開始日", eo.start_date || "-"], ["受講期限", eo.due_date || "-"], ["進捗", `${eo.progress || 0}%（${eo.done_items || 0}/${eo.total_items || 0}）`], ["修了日", eo.completed_at || "-"]].map(([l, v]) => (
          <div key={l} className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-400">{l}</div><div className="mt-1 font-semibold text-slate-800">{v}</div></div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <Button className="bg-green-600 hover:bg-green-700" onClick={managerConfirm} disabled={eo.manager_confirmed} data-testid="onb-manager-confirm">
          <CheckCircle2 className="w-4 h-4 mr-1" />{eo.manager_confirmed ? `確認済み（${eo.manager_confirmed_by || ""}）` : "管理者最終確認"}
        </Button>
        <Button variant="outline" onClick={() => { setOv({ status: eo.status, reason: "" }); setOvOpen(true); }} data-testid="onb-override-btn">状態を手動変更</Button>
      </div>

      <h3 className="font-semibold text-slate-800 mb-2">対象研修</h3>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-8">
        <Table>
          <TableHeader><TableRow><TableHead>研修名</TableHead><TableHead>区分</TableHead><TableHead>状態</TableHead><TableHead>テスト</TableHead><TableHead>所感</TableHead><TableHead>期限</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.trainings.map((x) => (
              <TableRow key={x.training_id} data-testid={`onb-tr-${x.training_id}`}>
                <TableCell><Link to={`/trainings/${x.training_id}`} className="text-blue-600 hover:underline">{x.name}</Link></TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded ${x.required ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}>{x.required ? "必須" : "任意"}</span></TableCell>
                <TableCell><OnbBadge status={x.status} /></TableCell>
                <TableCell className="text-sm text-slate-500">{x.quiz_score != null ? `${x.quiz_score}点${x.quiz_passed ? "・合格" : ""}` : "-"}</TableCell>
                <TableCell className="text-sm text-slate-500">{x.reflection_submitted ? "提出済" : "-"}</TableCell>
                <TableCell className="text-sm text-slate-500">{x.due_date || "-"}</TableCell>
              </TableRow>
            ))}
            {!d.trainings.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">対象研修が設定されていません</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <h3 className="font-semibold text-slate-800 mb-2">現場オリエンテーション</h3>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>項目</TableHead><TableHead>状態</TableHead><TableHead>説明担当者</TableHead><TableHead>備考</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {d.orientation.map((o) => <OrientationRow key={o.item_id} uid={userId} item={o} onSaved={load} />)}
            {!d.orientation.length && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">オリエン項目がありません</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {eo.status_history?.length > 0 && (
        <div className="mt-6 text-xs text-slate-400">
          <div className="font-medium text-slate-500 mb-1">状態変更履歴</div>
          {eo.status_history.map((h, i) => <div key={i}>{(h.at || "").slice(0, 16).replace("T", " ")}　{h.from} → {h.to}（{h.by}）{h.reason ? `／理由: ${h.reason}` : ""}</div>)}
        </div>
      )}

      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>入社研修の状態を手動変更</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>状態</Label>
              <Select value={ov.status} onValueChange={(v) => setOv({ ...ov, status: v })}>
                <SelectTrigger className="mt-1" data-testid="onb-override-status"><SelectValue /></SelectTrigger>
                <SelectContent>{OVERRIDE_STATUSES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>変更理由</Label><Input value={ov.reason} onChange={(e) => setOv({ ...ov, reason: e.target.value })} className="mt-1" data-testid="onb-override-reason" /></div>
            <p className="text-xs text-slate-400">手動変更後は自動判定が上書きされます（管理者最終確認で自動判定に戻ります）。</p>
          </div>
          <DialogFooter><Button onClick={saveOverride} className="bg-blue-600 hover:bg-blue-700" data-testid="onb-override-save">変更</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
